/** 固定首页页签：始终第一、不可关、始终存在 */
const HOME_AFFIX = Object.freeze({
  fullPath: '/home',
  path: '/home',
  name: 'Home',
  title: '首页',
  query: {},
  affix: true
})

function isHomeView(view) {
  if (!view) return false
  return view.name === 'Home' || view.path === '/home' || view.fullPath === '/home'
}

function ensureHomeAffix(state) {
  const idx = state.visitedViews.findIndex(isHomeView)
  if (idx === 0 && state.visitedViews[0].affix) {
    // 规范化字段，避免 query 等造成重复
    state.visitedViews.splice(0, 1, { ...HOME_AFFIX })
    return
  }
  if (idx > 0) {
    state.visitedViews.splice(idx, 1)
  }
  state.visitedViews.unshift({ ...HOME_AFFIX })
}

const state = {
  visitedViews: [],
  cachedViews: [],
  refreshKey: 0
}

const mutations = {
  ENSURE_HOME_AFFIX(state) {
    ensureHomeAffix(state)
  },
  ADD_VISITED_VIEW(state, view) {
    ensureHomeAffix(state)
    if (isHomeView(view)) return
    if (state.visitedViews.some(v => v.fullPath === view.fullPath)) return
    state.visitedViews.push({
      fullPath: view.fullPath,
      path: view.path,
      name: view.name,
      title: view.title || '未命名',
      query: view.query || {},
      affix: !!view.affix
    })
  },
  ADD_CACHED_VIEW(state, view) {
    if (!view.name) return
    if (state.cachedViews.includes(view.name)) return
    if (view.noCache) return
    state.cachedViews.push(view.name)
  },
  DEL_VISITED_VIEW(state, view) {
    if (view && (view.affix || isHomeView(view))) return
    const i = state.visitedViews.findIndex(v => v.fullPath === view.fullPath)
    if (i >= 0) state.visitedViews.splice(i, 1)
    ensureHomeAffix(state)
  },
  DEL_CACHED_VIEW(state, view) {
    if (!view.name) return
    // 同组件仍有其它页签时保留缓存
    const stillOpen = state.visitedViews.some(v => v.name === view.name)
    if (stillOpen) return
    const i = state.cachedViews.indexOf(view.name)
    if (i >= 0) state.cachedViews.splice(i, 1)
  },
  FORCE_DEL_CACHED_VIEW(state, view) {
    if (!view.name) return
    const i = state.cachedViews.indexOf(view.name)
    if (i >= 0) state.cachedViews.splice(i, 1)
  },
  DEL_OTHERS_VISITED(state, view) {
    state.visitedViews = state.visitedViews.filter(v => v.affix || v.fullPath === view.fullPath)
    ensureHomeAffix(state)
  },
  DEL_OTHERS_CACHED(state, view) {
    if (!view.name) {
      state.cachedViews = []
      return
    }
    const keep = new Set(
      state.visitedViews.filter(v => v.affix || v.fullPath === view.fullPath).map(v => v.name).filter(Boolean)
    )
    state.cachedViews = state.cachedViews.filter(n => keep.has(n))
  },
  DEL_ALL_VISITED(state) {
    state.visitedViews = state.visitedViews.filter(v => v.affix)
    ensureHomeAffix(state)
  },
  DEL_ALL_CACHED(state) {
    const keep = new Set(state.visitedViews.map(v => v.name).filter(Boolean))
    state.cachedViews = state.cachedViews.filter(n => keep.has(n))
  },
  CLEAR_TAGS(state) {
    state.visitedViews = []
    state.cachedViews = []
    state.refreshKey = 0
  },
  BUMP_REFRESH(state) {
    state.refreshKey += 1
  }
}

const actions = {
  ensureHomeAffix({ commit, state }) {
    commit('ENSURE_HOME_AFFIX')
    commit('ADD_CACHED_VIEW', HOME_AFFIX)
    return [...state.visitedViews]
  },
  addView({ dispatch }, view) {
    dispatch('addVisitedView', view)
    dispatch('addCachedView', view)
  },
  addVisitedView({ commit }, view) {
    commit('ADD_VISITED_VIEW', view)
  },
  addCachedView({ commit }, view) {
    commit('ADD_CACHED_VIEW', view)
  },
  delView({ dispatch, state }, view) {
    return new Promise(resolve => {
      dispatch('delVisitedView', view)
      dispatch('delCachedView', view)
      resolve({
        visitedViews: [...state.visitedViews],
        cachedViews: [...state.cachedViews]
      })
    })
  },
  delVisitedView({ commit, state }, view) {
    return new Promise(resolve => {
      commit('DEL_VISITED_VIEW', view)
      resolve([...state.visitedViews])
    })
  },
  delCachedView({ commit, state }, view) {
    return new Promise(resolve => {
      commit('DEL_CACHED_VIEW', view)
      resolve([...state.cachedViews])
    })
  },
  forceDelCachedView({ commit, state }, view) {
    return new Promise(resolve => {
      commit('FORCE_DEL_CACHED_VIEW', view)
      resolve([...state.cachedViews])
    })
  },
  delOthersViews({ dispatch, state }, view) {
    return new Promise(resolve => {
      dispatch('delOthersVisited', view)
      dispatch('delOthersCached', view)
      resolve({
        visitedViews: [...state.visitedViews],
        cachedViews: [...state.cachedViews]
      })
    })
  },
  delOthersVisited({ commit, state }, view) {
    commit('DEL_OTHERS_VISITED', view)
    return [...state.visitedViews]
  },
  delOthersCached({ commit, state }, view) {
    commit('DEL_OTHERS_CACHED', view)
    return [...state.cachedViews]
  },
  delAllViews({ dispatch, state }) {
    return new Promise(resolve => {
      dispatch('delAllVisited')
      dispatch('delAllCached')
      resolve({
        visitedViews: [...state.visitedViews],
        cachedViews: [...state.cachedViews]
      })
    })
  },
  delAllVisited({ commit, state }) {
    commit('DEL_ALL_VISITED')
    return [...state.visitedViews]
  },
  delAllCached({ commit, state }) {
    commit('DEL_ALL_CACHED')
    return [...state.cachedViews]
  },
  refreshView({ commit }) {
    commit('BUMP_REFRESH')
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
